import { captureException } from '@sentry/nextjs';
import { batch } from 'react-redux';
import {
  getEmailComponent,
  getFirstNameComponent,
  getLastNameComponent,
  getPhoneNumberComponent,
  getPostcodeComponent,
} from '../Components/__helpers__/component.helpers';
import { setBookedAppointmentId } from '../Lib/redux/slices/appointmentBooking';
import { setShouldRenderForm } from '../Lib/redux/slices/formConfig';
import {
  setFirstSubmissionAttemptTimestamp,
  setPartialSubmissionStatus,
  setSubmission,
  setSubmissionErrorMessage,
  setSubmissionStatus,
  updateSelectAndConnectBrandSubmissionStatus,
} from '../Lib/redux/slices/submission';
import { exposeEventToPartner } from './events';
import {
  exposeFormSubmitEvent,
  exposePartialFormSubmitEvent,
} from './submission';
import {
  setPendingMoveForwards,
  setShouldNavigateForward,
} from '../Lib/redux/slices/navigationControls';
import SubmissionStatus from '../Data/SubmissionStatus';
import { setAwaitingSubmissionRetry } from './submissionRetry';

function getAfterFirstSpace(str) {
  const parts = str.split(' ');
  return parts.slice(1).join(' ');
}

const handleFailedSubmission = ({
  dispatch,
  error,
  iFrameId,
  formId,
  currentPage,
  firstSubmissionAttemptTimestamp,
  currentSubmissionAttemptTimestamp,
  isResubmission,
  selectAndConnectBrand,
}) => {
  dispatch(
    setSubmissionErrorMessage({
      errorMessage:
        'There was an error receiving your answers, please try again.',
    })
  );

  setAwaitingSubmissionRetry({
    dispatch,
    firstSubmissionAttemptTimestamp,
    currentSubmissionAttemptTimestamp,
    isResubmission,
    isSelectAndConnectResubmission: !!selectAndConnectBrand,
  });

  if (selectAndConnectBrand) {
    dispatch(
      updateSelectAndConnectBrandSubmissionStatus({
        brandId: selectAndConnectBrand.brandId,
        submissionStatus:
          SubmissionStatus.AWAITING_RETRY_SELECT_AND_CONNECT_RESUBMISSION,
      })
    );
  }

  const eventObject = {
    type: 'submission',
    pageNumber: currentPage + 1,
    componentInputType: 'submission',
    componentId: null,
    iFrameId,
    formId,
  };
  exposeEventToPartner({
    eventType: 'formError',
    eventObject,
    errorObject: {
      message: `There was an error with the form on submission.`, // TODO would like to log the error here, but it may increase costs in Datadog
    },
  });
  captureException(error);
};

const handleSuccessfulSubmission = ({
  dispatch,
  data,
  submissionArgs,
  components,
  eventPublicKeyString,
  iFrameId,
  isDomainInternal,
  submissionPageNumber,
  isResubmission,
  selectAndConnectBrand,
  currentPage,
}) => {
  const submissionResponse = data.data.submitLead;
  const { appointmentId } = submissionResponse;
  exposeFormSubmitEvent({
    submissionArgs,
    components,
    eventPublicKeyString,
    submissionResponse,
    iFrameId,
    isDomainInternal,
    submissionPage: submissionPageNumber,
    currentPage,
    appointmentId,
  });
  batch(() => {
    /**
     * We have an id so set it and show thank you page
     */
    dispatch(
      setSubmission({
        id: submissionResponse.id,
        token: submissionResponse.submissionToken,
      })
    );
    dispatch(
      setSubmissionStatus({ submissionStatus: SubmissionStatus.COMPLETE })
    );
    dispatch(
      setFirstSubmissionAttemptTimestamp({
        firstSubmissionAttemptTimestamp: null,
      })
    );
    dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));
    if (typeof submissionPageNumber === 'number') {
      if (!isResubmission) {
        dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
      }
    } else {
      dispatch(setShouldRenderForm({ shouldRenderForm: false }));
    }
    dispatch(
      setBookedAppointmentId({
        bookedAppointmentId: appointmentId,
      })
    );

    if (selectAndConnectBrand) {
      dispatch(
        updateSelectAndConnectBrandSubmissionStatus({
          brandId: selectAndConnectBrand.brandId,
          submissionStatus: SubmissionStatus.COMPLETE,
        })
      );
    }
  });
};

export const bookAppointmentAndSubmitLead = ({
  selectedAppointment,
  components,
  localeCode,
  submitLeadWithAppointmentBookingMutation,
  submissionArgs,
  dispatch,
  currentPage,
  eventPublicKeyString,
  iFrameId,
  formId,
  isDomainInternal,
  submissionPageNumber,
  isResubmission,
  visitorId,
  firstSubmissionAttemptTimestamp,
  selectAndConnectBrand,
}) => {
  /**
   * Given an appointment has been selected this form includes appointment booking, and as we don't have an bookedAppointmentId yet we attempt to book an appointment.
   */
  const currentSubmissionAttemptTimestamp = new Date().getTime();
  const { selectedDate, selectedLocation, selectedEndDate } =
    selectedAppointment;
  const { countryAlpha2, isMigrated, shopIdPRM } = selectedLocation;
  const selectedLocationId = selectedLocation.id;

  const postcodeComponent = getPostcodeComponent({ components });
  const postcodeValue = postcodeComponent?.input?.value;

  const firstNameComponent = getFirstNameComponent({ components });
  const firstNameValue = firstNameComponent?.input?.value;

  const lastNameComponent = getLastNameComponent({ components });
  let lastNameValue = lastNameComponent?.input?.value;
  lastNameValue = lastNameValue || getAfterFirstSpace(firstNameValue);

  const phoneComponent = getPhoneNumberComponent({ components });
  const phoneValue = phoneComponent?.input?.value;

  const emailComponent = getEmailComponent({ components });
  const emailValue = emailComponent?.input?.value;

  const appointmentBookingArgs = {
    amplifon: {
      name: firstNameValue,
      email: emailValue,
      surname: lastNameValue, // they want the surname split out
      appointmentDate: selectedDate,
      appointmentAddress: selectedLocation.address,
      appointmentEndDate: selectedEndDate,
      phone: phoneValue,
      countryAlpha2, // this should be upperCase alpha2 format
      locale: localeCode,
      postcode: postcodeValue,
      shopCode: selectedLocationId, // i.e. 027 - the amplifon clinicId
      isMigrated,
      shopIdPRM: shopIdPRM || '',
    },
  };

  /**
   * This includes bookedAppointmentId in the response and appointmentBooking in its args
   */
  submitLeadWithAppointmentBookingMutation({
    variables: {
      submission: submissionArgs,
      appointmentBooking: appointmentBookingArgs,
      visitorId,
    },
  })
    .then((data) => {
      handleSuccessfulSubmission({
        dispatch,
        data,
        submissionArgs,
        components,
        eventPublicKeyString,
        iFrameId,
        isDomainInternal,
        submissionPageNumber,
        isResubmission,
        selectAndConnectBrand,
        fromPage: currentPage,
      });
    })
    .catch((error) => {
      handleFailedSubmission({
        dispatch,
        error,
        iFrameId,
        formId,
        currentPage,
        firstSubmissionAttemptTimestamp,
        currentSubmissionAttemptTimestamp,
        isResubmission,
        selectAndConnectBrand,
      });
    });
};

export const submitStandardLead = ({
  components,
  currentPage,
  dispatch,
  eventPublicKeyString,
  formId,
  iFrameId,
  isDomainInternal,
  submissionArgs,
  submissionPageNumber,
  submitLeadMutation,
  isResubmission,
  visitorId,
  firstSubmissionAttemptTimestamp,
  selectAndConnectBrand,
}) => {
  const currentSubmissionAttemptTimestamp = new Date().getTime();
  /**
   * Submission without the appointment booking variables / bookedAppointmentId in the response
   */
  submitLeadMutation({
    variables: {
      submission: submissionArgs,
      visitorId,
    },
  })
    .then((data) => {
      handleSuccessfulSubmission({
        dispatch,
        data,
        submissionArgs,
        components,
        eventPublicKeyString,
        iFrameId,
        isDomainInternal,
        submissionPageNumber,
        isResubmission,
        selectAndConnectBrand,
        currentPage,
      });
    })
    .catch((error) => {
      handleFailedSubmission({
        dispatch,
        error,
        iFrameId,
        formId,
        currentPage,
        firstSubmissionAttemptTimestamp,
        currentSubmissionAttemptTimestamp,
        isResubmission,
        selectAndConnectBrand,
      });
    });
};

/**
 * Submits a partial form submission.
 *
 * See readme for more details
 */
export const submitStandardLeadWithPartialData = ({
  dispatch,
  submissionArgs,
  submitLeadMutation,
  visitorId,
  firstSubmissionAttemptTimestamp,
  iFrameId,
  partialSubmissionPage,
  components,
  isDomainInternal,
}) => {
  const currentSubmissionAttemptTimestamp = new Date().getTime();
  submitLeadMutation({
    variables: {
      submission: submissionArgs,
      visitorId,
    },
  })
    .then((response) => {
      const submissionId = response?.data?.submitLead?.id;

      exposePartialFormSubmitEvent({
        iFrameId,
        partialSubmissionPage,
        submissionId,
        components,
        isDomainInternal,
      });

      dispatch(
        setPartialSubmissionStatus({
          partialSubmissionStatus: SubmissionStatus.COMPLETE,
        })
      );
    })
    .catch(() => {
      setAwaitingSubmissionRetry({
        dispatch,
        firstSubmissionAttemptTimestamp,
        currentSubmissionAttemptTimestamp,
        isResubmission: false,
        isSelectAndConnectResubmission: false,
        isPartial: true,
      });
    });
};
